import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiFilter, FiSearch, FiCheckCircle, FiMapPin, FiTruck, FiClock } from 'react-icons/fi';
import { alertService } from '../services/api';
import { format, formatDistanceToNow } from 'date-fns';
import LoadingSpinner from '../components/common/LoadingSpinner';

const Alerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    type: 'all',
    status: 'all',
    date: 'all'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(10);

  // Fetch alerts data
  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        setLoading(true);
        
        // In a real app, we would fetch from API
        // const response = await alertService.getAlerts({
        //   page: currentPage,
        //   limit: itemsPerPage,
        //   ...searchTerm ? { search: searchTerm } : {},
        //   ...filters.type !== 'all' ? { type: filters.type } : {},
        //   ...filters.status !== 'all' ? { acknowledged: filters.status === 'acknowledged' } : {},
        //   ...filters.date !== 'all' ? { date: filters.date } : {}
        // });
        
        // For now, use dummy data
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const dummyAlerts = [
          {
            id: 'a1',
            type: 'geofence_entry',
            vehicle: { id: 'v1', name: 'Truck 101', registration: 'ABC-1234' },
            geofence: { id: 'g1', name: 'Downtown Zone' },
            message: 'Vehicle entered Downtown Zone',
            severity: 'info',
            position: { lat: 40.7128, lng: -74.006 },
            acknowledged: false,
            createdAt: new Date().toISOString()
          },
          {
            id: 'a2',
            type: 'geofence_exit',
            vehicle: { id: 'v2', name: 'Delivery Van 202', registration: 'XYZ-5678' },
            geofence: { id: 'g3', name: 'Warehouse District' },
            message: 'Vehicle exited Warehouse District',
            severity: 'info',
            position: { lat: 40.7234, lng: -74.0123 },
            acknowledged: true,
            acknowledgedBy: { id: 'u1', name: 'Admin User' },
            acknowledgedAt: new Date(Date.now() - 1000 * 60 * 30).toISOString(), // 30 minutes ago
            createdAt: new Date(Date.now() - 1000 * 60 * 60).toISOString() // 1 hour ago
          },
          {
            id: 'a3',
            type: 'speeding',
            vehicle: { id: 'v3', name: 'Fleet Car 303', registration: 'DEF-9012' },
            message: 'Vehicle exceeding speed limit (45 mph in 35 mph zone)',
            severity: 'warning',
            position: { lat: 40.7118, lng: -74.009 },
            acknowledged: false,
            createdAt: new Date(Date.now() - 1000 * 60 * 15).toISOString() // 15 minutes ago
          },
          {
            id: 'a4',
            type: 'idle_time',
            vehicle: { id: 'v2', name: 'Delivery Van 202', registration: 'XYZ-5678' },
            message: 'Vehicle idle for more than 30 minutes',
            severity: 'info',
            position: { lat: 40.7235, lng: -74.0125 },
            acknowledged: true,
            acknowledgedBy: { id: 'u1', name: 'Admin User' },
            acknowledgedAt: new Date(Date.now() - 1000 * 60 * 10).toISOString(), // 10 minutes ago
            createdAt: new Date(Date.now() - 1000 * 60 * 40).toISOString() // 40 minutes ago
          },
          {
            id: 'a5',
            type: 'offline',
            vehicle: { id: 'v4', name: 'Pickup Truck 404', registration: 'GHI-3456' },
            message: 'Vehicle went offline',
            severity: 'error',
            position: { lat: 40.7145, lng: -74.0055 },
            acknowledged: false,
            createdAt: new Date(Date.now() - 1000 * 60 * 5).toISOString() // 5 minutes ago
          }
        ];

        setAlerts(dummyAlerts);
        setTotalPages(1); // In a real app, would be calculated from response
        setLoading(false);
      } catch (err) {
        setError('Failed to load alerts');
        setLoading(false);
        console.error('Error fetching alerts:', err);
      }
    };

    fetchAlerts();
  }, [currentPage, itemsPerPage, searchTerm, filters]);

  // Filter alerts based on search term and filters
  const filteredAlerts = alerts.filter(alert => {
    // Search term
    const matchesSearch = 
      alert.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (alert.vehicle?.name && alert.vehicle.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (alert.geofence?.name && alert.geofence.name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Type filter
    const matchesType = 
      filters.type === 'all' ||
      (filters.type === 'geofence' && (alert.type === 'geofence_entry' || alert.type === 'geofence_exit')) ||
      (filters.type !== 'geofence' && alert.type === filters.type);
    
    // Status filter
    const matchesStatus = 
      filters.status === 'all' ||
      (filters.status === 'acknowledged' && alert.acknowledged) ||
      (filters.status === 'unacknowledged' && !alert.acknowledged);
    
    // Date filter
    let matchesDate = true;
    if (filters.date !== 'all') {
      const alertDate = new Date(alert.createdAt);
      const now = new Date();
      
      if (filters.date === 'today') {
        matchesDate = 
          alertDate.getDate() === now.getDate() && 
          alertDate.getMonth() === now.getMonth() && 
          alertDate.getFullYear() === now.getFullYear();
      } else if (filters.date === 'yesterday') {
        const yesterday = new Date(now.setDate(now.getDate() - 1));
        matchesDate = 
          alertDate.getDate() === yesterday.getDate() && 
          alertDate.getMonth() === yesterday.getMonth() && 
          alertDate.getFullYear() === yesterday.getFullYear();
      } else if (filters.date === 'week') {
        const weekAgo = new Date(now.setDate(now.getDate() - 7));
        matchesDate = alertDate >= weekAgo;
      }
    }
    
    return matchesSearch && matchesType && matchesStatus && matchesDate;
  });

  // Handle acknowledge alert
  const handleAcknowledge = async (id) => {
    try {
      // In a real app, we would call API
      // await alertService.acknowledgeAlert(id);
      
      // For demo, just update local state
      setAlerts(prevAlerts => prevAlerts.map(alert => {
        if (alert.id === id) {
          return {
            ...alert,
            acknowledged: true,
            acknowledgedBy: { id: 'u1', name: 'Admin User' },
            acknowledgedAt: new Date().toISOString()
          };
        }
        return alert;
      }));
    } catch (err) {
      console.error('Error acknowledging alert:', err);
    }
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on new search
  };

  // Handle filter change
  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setCurrentPage(1); // Reset to first page on filter change
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Get alert type icon and color
  const getAlertTypeInfo = (type) => {
    switch (type) {
      case 'geofence_entry':
        return { 
          icon: <FiMapPin />, 
          color: 'bg-blue-100 text-blue-800',
          label: 'Geofence Entry' 
        };
      case 'geofence_exit':
        return { 
          icon: <FiMapPin />, 
          color: 'bg-purple-100 text-purple-800',
          label: 'Geofence Exit' 
        };
      case 'speeding':
        return { 
          icon: <FiTruck />, 
          color: 'bg-orange-100 text-orange-800',
          label: 'Speeding' 
        };
      case 'idle_time':
        return { 
          icon: <FiClock />, 
          color: 'bg-yellow-100 text-yellow-800',
          label: 'Idle Time' 
        };
      case 'offline':
        return { 
          icon: <FiTruck />, 
          color: 'bg-red-100 text-red-800',
          label: 'Offline' 
        };
      default:
        return { 
          icon: <FiTruck />, 
          color: 'bg-gray-100 text-gray-800',
          label: 'Other' 
        };
    }
  };

  // Get formatted time
  const getFormattedTime = (timestamp) => {
    const date = new Date(timestamp);
    return {
      relative: formatDistanceToNow(date, { addSuffix: true }),
      absolute: format(date, 'MMM d, yyyy h:mm a')
    };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10">
        <p className="text-ui-error">{error}</p>
        <button 
          className="mt-4 btn btn-primary" 
          onClick={() => window.location.reload()}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Alerts</h1>
        <p className="text-gray-600">Monitor and manage system alerts</p>
      </div>

      {/* Search and filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiSearch className="text-gray-400" />
            </div>
            <input
              type="text"
              className="form-input pl-10 w-full"
              placeholder="Search alerts..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <div className="flex flex-col md:flex-row gap-2">
            <select 
              className="form-input"
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
            >
              <option value="all">All Types</option>
              <option value="geofence">Geofence</option>
              <option value="speeding">Speeding</option>
              <option value="idle_time">Idle Time</option>
              <option value="offline">Offline</option>
            </select>
            <select 
              className="form-input"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="acknowledged">Acknowledged</option>
              <option value="unacknowledged">Unacknowledged</option>
            </select>
            <select 
              className="form-input"
              value={filters.date}
              onChange={(e) => handleFilterChange('date', e.target.value)}
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="week">Last 7 Days</option>
            </select>
          </div>
        </div>
      </div>

      {/* Alerts list */}
      {filteredAlerts.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500">No alerts found. Adjust your filters to see more results.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Alert
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vehicle
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAlerts.map(alert => {
                  const { icon, color, label } = getAlertTypeInfo(alert.type);
                  const time = getFormattedTime(alert.createdAt);
                  
                  return (
                    <tr key={alert.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{alert.message}</div>
                        {alert.geofence && (
                          <div className="text-sm text-gray-500">
                            Geofence: {alert.geofence.name}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${color} items-center`}>
                          <span className="mr-1">{icon}</span>
                          {label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {alert.vehicle ? (
                          <div>
                            <div className="text-sm font-medium text-gray-900">{alert.vehicle.name}</div>
                            <div className="text-sm text-gray-500">{alert.vehicle.registration}</div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">N/A</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900" title={time.absolute}>
                          {time.relative}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {alert.acknowledged ? (
                          <div>
                            <span className="flex items-center text-ui-success text-sm">
                              <FiCheckCircle className="mr-1" />
                              Acknowledged
                            </span>
                            <div className="text-xs text-gray-500">
                              by {alert.acknowledgedBy?.name || 'Unknown'}
                            </div>
                          </div>
                        ) : (
                          <span className="px-2 text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <Link 
                            to={`/alerts/${alert.id}`}
                            className="text-primary hover:text-primary-dark"
                          >
                            View
                          </Link>
                          {!alert.acknowledged && (
                            <button 
                              onClick={() => handleAcknowledge(alert.id)}
                              className="text-green-600 hover:text-green-900"
                            >
                              Acknowledge
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${
                    currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-primary hover:bg-gray-50'
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-4 py-2 ml-3 text-sm font-medium rounded-md ${
                    currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-primary hover:bg-gray-50'
                  }`}
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing page <span className="font-medium">{currentPage}</span> of{' '}
                    <span className="font-medium">{totalPages}</span>
                  </p>
                </div>
                <div>
                  <nav className="inline-flex rounded-md shadow-sm">
                    {[...Array(totalPages).keys()].map(page => (
                      <button
                        key={page + 1}
                        onClick={() => handlePageChange(page + 1)}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
                          currentPage === page + 1
                            ? 'bg-primary text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        } ${
                          page === 0 ? 'rounded-l-md' : ''
                        } ${
                          page === totalPages - 1 ? 'rounded-r-md' : ''
                        }`}
                      >
                        {page + 1}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Alerts;